import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { Center, Heading, VStack } from "@chakra-ui/layout";
import { Link } from "react-router-dom";
import { Card } from "../../components/Card/Card";
import { useBusinessContext } from "../../context/ModelContext";
import { useApiRequest } from "../../hooks/useApiRequest";
import { Button, HStack, Select, Spinner, Text } from "@chakra-ui/react";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { RolesRbacTable } from "../../components/RolesRbacTable/RolesRbacTable";
import { PlusIcon } from "../../styles/icons";
import { RoleRbacModal } from "../../components/RoleRbacModal/RoleRbacModal";
import { CherryPayApi } from "../../api/models";
import { useApiUpdateRequest } from "../../hooks/useApiUpdateRequest";
import { useToast } from "../../hooks/useToast";
import { useConfirmationDialog } from "../../hooks/useConfirmationDialog";

export const BusinessRolesRbac = () => {
  const business = useBusinessContext();
  const { showSuccessToast, showErrorToast } = useToast();
  const { confirmationElement, showConfirmationDialog } =
    useConfirmationDialog();
  const [selectedProduct, setSelectedProduct] =
    useState<CherryPayApi.LicencedProduct>("CherryPay");
  const [roleModalState, setRoleModalState] = useState<{
    isOpen: boolean;
    existingRole: CherryPayApi.CherryHubRoleSummaryItem | null;
  }>({ isOpen: false, existingRole: null });

  const {
    data: rolesData,
    isLoading,
    error,
    refresh,
  } = useApiRequest(
    (apiClient) =>
      apiClient.getIamRolesForProduct(business.BusinessId, selectedProduct),
    [business, selectedProduct]
  );

  const roles = useMemo(() => {
    const allRoles = rolesData?.Roles ?? [];

    // Separate system and non-system roles
    const systemRoles = allRoles.filter((role) => role.IsSystemRole);
    const nonSystemRoles = allRoles.filter((role) => !role.IsSystemRole);

    // Return system roles first, then non-system roles
    return [...systemRoles, ...nonSystemRoles];
  }, [rolesData]);

  const availablePermissions = useMemo(
    () => rolesData?.AvailablePermissions ?? [],
    [rolesData]
  );

  const onClickNewRole = useCallback(() => {
    setRoleModalState({ isOpen: true, existingRole: null });
  }, []);

  const onCloseModal = useCallback(() => {
    setRoleModalState({ isOpen: false, existingRole: null });
  }, []);

  const onClickViewRole = useCallback(
    (role: CherryPayApi.CherryHubRoleSummaryItem) => {
      setRoleModalState({ isOpen: true, existingRole: role });
    },
    []
  );

  const onClickEditRole = useCallback(
    (role: CherryPayApi.CherryHubRoleSummaryItem) => {
      setRoleModalState({ isOpen: true, existingRole: role });
    },
    []
  );

  const createRoleRequest = useApiUpdateRequest(
    (apiClient, roleUpdate: CherryPayApi.CherryHubRoleUpdate) =>
      apiClient.createIamRole(business.BusinessId, roleUpdate),
    [business]
  );

  const onCreateRole = useCallback(
    async (roleUpdate: CherryPayApi.CherryHubRoleUpdate) => {
      createRoleRequest.request(roleUpdate);
    },
    [createRoleRequest]
  );

  const updateRoleRequest = useApiUpdateRequest(
    (
      apiClient,
      params: { roleId: string; roleUpdate: CherryPayApi.CherryHubRoleUpdate }
    ) =>
      apiClient.updateIamRole(
        business.BusinessId,
        params.roleId,
        params.roleUpdate
      ),
    [business]
  );

  const onUpdateRole = useCallback(
    async (roleId: string, roleUpdate: CherryPayApi.CherryHubRoleUpdate) => {
      updateRoleRequest.request({ roleId, roleUpdate });
    },
    [updateRoleRequest]
  );

  const deleteRoleRequest = useApiUpdateRequest(
    (apiClient, roleId: string) =>
      apiClient.deleteIamRole(business.BusinessId, roleId),
    [business]
  );

  const onClickDeleteRole = useCallback(
    async (role: CherryPayApi.CherryHubRoleSummaryItem) => {
      const result = await showConfirmationDialog(
        "Delete Role",
        `Are you sure you want to delete the role "${role.DisplayName}"? This action cannot be undone.`,
        "Delete"
      );

      if (role.Meta?.Id && result.ok) {
        deleteRoleRequest.request(role.Meta.Id);
      }
    },
    [showConfirmationDialog, deleteRoleRequest]
  );

  useEffect(() => {
    if (createRoleRequest.isLoading) {
      return;
    }

    if (createRoleRequest.result?.ok) {
      showSuccessToast("Role created successfully.");
      refresh();
    } else if (
      createRoleRequest.result?.ok === false ||
      createRoleRequest.error
    ) {
      showErrorToast(
        createRoleRequest.result?.message ?? "Failed to create role."
      );
    }
  }, [
    createRoleRequest.isLoading,
    createRoleRequest.result,
    createRoleRequest.error,
    refresh,
    showSuccessToast,
    showErrorToast,
  ]);

  useEffect(() => {
    if (updateRoleRequest.isLoading) {
      return;
    }

    if (updateRoleRequest.result?.ok) {
      showSuccessToast("Role updated successfully.");
      refresh();
    } else if (
      updateRoleRequest.result?.ok === false ||
      updateRoleRequest.error
    ) {
      showErrorToast(
        updateRoleRequest.result?.message ?? "Failed to update role."
      );
    }
  }, [
    updateRoleRequest.isLoading,
    updateRoleRequest.result,
    updateRoleRequest.error,
    refresh,
    showSuccessToast,
    showErrorToast,
  ]);

  useEffect(() => {
    if (deleteRoleRequest.isLoading) {
      return;
    }

    if (deleteRoleRequest.result?.ok) {
      showSuccessToast("Role deleted successfully.");
      refresh();
    } else if (
      deleteRoleRequest.result?.ok === false ||
      deleteRoleRequest.error
    ) {
      showErrorToast(
        deleteRoleRequest.result?.message ?? "Failed to delete role."
      );
    }
  }, [
    deleteRoleRequest.isLoading,
    deleteRoleRequest.result,
    deleteRoleRequest.error,
    refresh,
    showSuccessToast,
    showErrorToast,
  ]);

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/businesses/${business.BusinessId}/roles-rbac`}
            >
              Roles
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>Roles</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <Card p="2" width="100%">
          <VStack w="100%" spacing="2" alignItems="start">
            <HStack w="100%" justifyContent="space-between">
              <Heading size="sm">Product Roles & Permissions</Heading>
              <HStack>
                <Select
                  value={selectedProduct}
                  onChange={(e) =>
                    setSelectedProduct(
                      e.target.value as CherryPayApi.LicencedProduct
                    )
                  }
                  maxW="200px"
                  display={"none"}
                >
                  <option value="CherryPay">CherryPay</option>
                </Select>
                <Button
                  leftIcon={<PlusIcon />}
                  onClick={onClickNewRole}
                  colorScheme="cherryButton"
                  w={130}
                >
                  New Role
                </Button>
              </HStack>
            </HStack>
            {isLoading && (
              <Center w="100%">
                <Spinner />
              </Center>
            )}
            {error && (
              <ErrorMessage>
                An error was encountered while loading the roles.
              </ErrorMessage>
            )}
            {!isLoading && !error && roles.length > 0 && (
              <RolesRbacTable
                roles={roles}
                availablePermissions={availablePermissions}
                onClickView={onClickViewRole}
                onClickEdit={onClickEditRole}
                onClickDelete={onClickDeleteRole}
              />
            )}
            {!isLoading && !error && roles.length === 0 && (
              <Center w="100%">
                <Text>No roles found for the selected product.</Text>
              </Center>
            )}
          </VStack>
        </Card>
      </PageContent>
      {availablePermissions.length > 0 && (
        <RoleRbacModal
          isOpen={roleModalState.isOpen}
          existingRole={roleModalState.existingRole}
          availablePermissions={availablePermissions}
          product={selectedProduct}
          onClose={onCloseModal}
          onCreateRole={onCreateRole}
          onUpdateRole={onUpdateRole}
        />
      )}
      {confirmationElement}
    </>
  );
};
